import { routes } from "../../assets/constants";
import { ApiMethod } from "../models/api-types.type";
import { useAuthContext } from "./use-auth-context.hook";

export interface SendEmailRequest {
  email: string;
  htmlPayload: string;
  subject: string;
  productIds?: string[];
  fromName?: string;
  voucherCode?: string;
  templateId?: string;
  productLayout?: string;
  productUrlMap?: Record<string, string>;
}

export interface SendEmailResponse {
  success: boolean;
  message?: string;
  messageId?: string;
  trackingId?: string;
  /** True when the send was blocked by the vendor's monthly email quota. */
  quotaExceeded?: boolean;
  /** True when the vendor has no marketing postal address configured. */
  missingPostalAddress?: boolean;
}

// Email tracking analytics interfaces
export interface EmailStatsRequest {
  statType: 'EMAIL_OPEN_RATE' | 'EMAIL_CLICK_RATE' | 'EMAIL_SENT_COUNT' | 'EMAIL_OPENED_COUNT' | 'EMAIL_CLICKED_COUNT';
  startDate: string;
  endDate: string;
  email?: string;
}

export interface EmailOpenRateStats {
  totalSent: number;
  totalOpened: number;
  openRate: number;
}

export interface EmailClickRateStats {
  totalSent: number;
  totalClicked: number;
  clickRate: number;
}

export interface EmailCountStats {
  _id: string; // Date string
  count: number;
  events?: any[]; // Full event details if needed
}

export interface EmailEventDetails {
  _id: string;
  eventType: 'EmailSentEvent' | 'EmailOpenedEvent' | 'EmailLinkClickedEvent';
  visitorId: string;
  vendorId: string;
  dateTime: string;
  data: {
    email: string;
    emailId?: string;
    trackingId?: string;
    productIds?: string[];
    subject?: string;
    originalUrl?: string;
    linkType?: string;
    productId?: string;
    userAgent?: string;
    ipAddress?: string;
  };
}

export interface EmailAnalyticsSummary {
  totalEmailsSent: number;
  totalEmailsOpened: number;
  totalLinksClicked: number;
  openRate: number;
  clickRate: number;
  topClickedProducts: Array<{
    productId: string;
    clicks: number;
  }>;
  recentActivity: EmailEventDetails[];
}

export const useEmailApi = () => {
  const { sendAuthGuardedRequest } = useAuthContext();

  const sendEmail = async (emailData: SendEmailRequest, visitorId?: string): Promise<SendEmailResponse> => {
    try {
      console.log('Calling sendAuthGuardedRequest with:', emailData);
      console.log('VisitorId:', visitorId);
      
      // Prepare headers with visitorId if provided
      const headers: Record<string, string> = {};
      if (visitorId) {
        headers['visitor-id'] = visitorId;
      }
      
      const response: any = await sendAuthGuardedRequest(
        ApiMethod.POST,
        routes.email.send,
        emailData,
        { headers }
      );
      
      console.log('Raw API response:', response);
      
      // Handle case where sendAuthGuardedRequest returns undefined (auth errors, etc.)
      if (response === undefined || response === null) {
        console.warn('API response is undefined - likely an authentication error');
        return { success: false, message: 'Authentication error. Please log in again.' };
      }
      
      // Handle different response formats
      if (typeof response.success === 'boolean') {
        return response;
      }
      
      // If response doesn't have success property but we got a valid response, assume success
      // This handles cases where the API returns 200 OK without a specific success field
      return { success: true, message: 'Email sent successfully' };
      
    } catch (error: any) {
      console.error('Failed to send email - caught error:', error);

      // Plan email-quota reached → friendly, actionable message instead of a
      // generic failure. The backend 403 carries { quotaType, limit, ... }.
      const body = error?.body || error?.response?.data || {};
      const isEmailQuota =
        body?.quotaType === 'maxEmailsPerMonth' ||
        /quota exceeded for maxEmailsPerMonth/i.test(error?.message || '');
      if (isEmailQuota) {
        const limit = body?.limit;
        return {
          success: false,
          quotaExceeded: true,
          message:
            limit != null
              ? `You've reached your monthly email limit (${limit}). Upgrade your plan to send more.`
              : `You've reached your monthly email limit. Upgrade your plan to send more.`,
        };
      }

      // No postal address configured → point them at the setting that fixes it.
      // The backend 400 carries { code: 'MISSING_POSTAL_ADDRESS', error }.
      if (body?.code === 'MISSING_POSTAL_ADDRESS') {
        return {
          success: false,
          missingPostalAddress: true,
          message:
            body?.error ||
            'Add your postal address in Email settings before sending. It is required in the footer of every marketing email.',
        };
      }

      // Handle different error formats
      let errorMessage = 'Failed to send email. Please try again.';

      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.status) {
        errorMessage = `Server error: ${error.status}`;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      return { success: false, message: errorMessage };
    }
  };

  const getEmailStats = async (params: EmailStatsRequest): Promise<any> => {
    try {
      const queryParams = new URLSearchParams({
        statType: params.statType,
        startDate: params.startDate,
        endDate: params.endDate,
        ...(params.email && { email: params.email })
      });

      const response = await sendAuthGuardedRequest(
        ApiMethod.GET,
        `${routes.analytics.stats}?${queryParams.toString()}`
      );

      return response;
    } catch (error) {
      console.error('Failed to fetch email stats:', error);
      throw error;
    }
  };

  const getEmailAnalyticsSummary = async (
    startDate: string, 
    endDate: string, 
    email?: string
  ): Promise<EmailAnalyticsSummary> => {
    try {
      console.log('Fetching email analytics summary for period:', startDate, 'to', endDate);

      // Fetch all required stats in parallel
      const [
        sentStats,
        openedStats,
        clickedStats,
        openRateStats,
        clickRateStats
      ] = await Promise.all([
        getEmailStats({ statType: 'EMAIL_SENT_COUNT', startDate, endDate, email }),
        getEmailStats({ statType: 'EMAIL_OPENED_COUNT', startDate, endDate, email }),
        getEmailStats({ statType: 'EMAIL_CLICKED_COUNT', startDate, endDate, email }),
        getEmailStats({ statType: 'EMAIL_OPEN_RATE', startDate, endDate, email }),
        getEmailStats({ statType: 'EMAIL_CLICK_RATE', startDate, endDate, email })
      ]);

      // Calculate totals
      const totalSent = Array.isArray(sentStats) 
        ? sentStats.reduce((sum, stat) => sum + (stat.count || 0), 0)
        : 0;

      const totalOpened = Array.isArray(openedStats) 
        ? openedStats.reduce((sum, stat) => sum + (stat.count || 0), 0)
        : 0;

      const totalClicked = Array.isArray(clickedStats) 
        ? clickedStats.reduce((sum, stat) => sum + (stat.count || 0), 0)
        : 0;

      // Calculate rates
      const openRate = openRateStats?.[0]?.openRate || (totalSent > 0 ? (totalOpened / totalSent) * 100 : 0);
      const clickRate = clickRateStats?.[0]?.clickRate || (totalSent > 0 ? (totalClicked / totalSent) * 100 : 0);

      // Extract product engagement data
      const topClickedProducts: Array<{ productId: string; clicks: number }> = [];
      
      // Get recent activity (last 10 events)
      const recentActivity: EmailEventDetails[] = [];

      console.log('Email analytics summary calculated:', {
        totalSent,
        totalOpened,
        totalClicked,
        openRate,
        clickRate
      });

      return {
        totalEmailsSent: totalSent,
        totalEmailsOpened: totalOpened,
        totalLinksClicked: totalClicked,
        openRate: Math.round(openRate * 100) / 100,
        clickRate: Math.round(clickRate * 100) / 100,
        topClickedProducts,
        recentActivity
      };

    } catch (error) {
      console.error('Failed to fetch email analytics summary:', error);
      // Return empty summary on error
      return {
        totalEmailsSent: 0,
        totalEmailsOpened: 0,
        totalLinksClicked: 0,
        openRate: 0,
        clickRate: 0,
        topClickedProducts: [],
        recentActivity: []
      };
    }
  };

  const getEmailEventsForContact = async (
    email: string,
    startDate?: string,
    endDate?: string
  ): Promise<EmailEventDetails[]> => {
    try {
      const params: EmailStatsRequest = {
        statType: 'EMAIL_SENT_COUNT',
        startDate: startDate || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        endDate: endDate || new Date().toISOString(),
        email
      };

      const events = await getEmailStats(params);
      
      // Extract events from the response
      const allEvents: EmailEventDetails[] = [];
      if (Array.isArray(events)) {
        events.forEach(stat => {
          if (stat.events && Array.isArray(stat.events)) {
            allEvents.push(...stat.events);
          }
        });
      }

      return allEvents.sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime());
    } catch (error) {
      console.error('Failed to fetch email events for contact:', error);
      return [];
    }
  };

  const generateEmailCopy = async (request: {
    products: Array<{ name: string; category?: string; usageCount?: number }>;
    leadEmail: string;
    companyName: string;
    language?: string;
  }): Promise<{
    success: boolean;
    data?: {
      subject: string;
      headerTitle: string;
      headerSubtitle: string;
      welcomeMessage: string;
      productSectionTitle: string;
      ctaButtonText: string;
      footerText: string;
    };
    error?: string;
  }> => {
    try {
      const response: any = await sendAuthGuardedRequest(
        ApiMethod.POST,
        '/email/generate-copy',
        request
      );
      return response;
    } catch (error: any) {
      return { success: false, error: error?.message || 'Failed to generate email copy' };
    }
  };

  const testSmtpConnection = async (smtpConfig: { host: string; port: number; user: string; pass: string }): Promise<{ connected: boolean; message: string }> => {
    try {
      const response: any = await sendAuthGuardedRequest(
        ApiMethod.POST,
        '/email/test-connection',
        smtpConfig
      );
      return response;
    } catch (error) {
      return { connected: false, message: 'Failed to test connection.' };
    }
  };

  return {
    sendEmail,
    getEmailStats,
    getEmailAnalyticsSummary,
    getEmailEventsForContact,
    testSmtpConnection,
    generateEmailCopy
  };
}; 